<template>
  <div class="p-6">
    <!-- ISO 42001 Annex A clauses this policy maps to (from the framework registry) -->
    <div v-if="isoClauses.length > 0" class="mb-8">
      <h3 class="text-lg font-semibold mb-2" style="color: var(--text-primary);">
        ISO 42001 Clauses
      </h3>
      <p class="text-sm mb-3" style="color: var(--text-secondary);">
        This policy maps to the following ISO/IEC 42001 Annex A clauses.
      </p>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="iso in isoClauses"
          :key="iso"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium"
          style="background: var(--sage-green-light, #E8EEE8); color: var(--text-primary);"
        >
          <i class="pi pi-check-circle" style="color: var(--primary-gold);"></i>
          {{ iso }}
        </span>
      </div>
    </div>

    <div v-if="controls.length > 0">
      <div class="mb-6">
        <h3 class="text-lg font-semibold mb-2" style="color: var(--text-primary);">
          ASHC Controls
        </h3>
        <p class="text-sm mb-4" style="color: var(--text-secondary);">
          This policy binds {{ controls.length }} control{{ controls.length > 1 ? 's' : '' }} from the
          Asher CHAR controls catalog. Each control names the process domains and base practices that
          implement it, the evidence expected at each maturity level, and the external requirements it
          addresses.
        </p>
      </div>

      <div class="space-y-6">
        <div v-for="control in controls" :key="control.control_id"
             class="border rounded-lg overflow-hidden"
             style="border-color: var(--border-light);">

          <!-- Header -->
          <div class="p-4 flex items-start gap-4"
               style="background: var(--sage-green-light, #E8EEE8);">
            <div class="flex-shrink-0 w-28 text-center">
              <div class="text-xs font-semibold mb-1" style="color: var(--text-tertiary);">
                {{ familyName(control.family) }}
              </div>
              <div class="px-3 py-1.5 rounded font-mono text-sm font-bold"
                   style="background: var(--primary-gold); color: var(--primary-white);">
                {{ control.control_id }}
              </div>
              <div v-if="control.derived_from?.iso_42001_clauses?.length" class="text-xs mt-2" style="color: var(--text-tertiary);">
                ISO {{ control.derived_from.iso_42001_clauses.join(', ') }}
              </div>
            </div>

            <div class="flex-1">
              <h4 class="text-lg font-semibold mb-1" style="color: var(--text-primary);">
                {{ control.title }}
                <span v-if="control.foundational"
                      class="ml-2 align-middle px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
                      style="background: var(--primary-gold); color: var(--primary-white);">
                  Foundational
                </span>
              </h4>
              <p class="text-sm" style="color: var(--text-secondary);">
                {{ control.description }}
              </p>
              <p v-if="control.applicability?.rationale" class="text-xs italic mt-2" style="color: var(--text-tertiary);">
                <i class="pi pi-info-circle mr-1"></i>
                Applies {{ control.applicability.condition === 'always' ? 'always' : `when ${control.applicability.condition}` }}
                — {{ control.applicability.rationale }}
              </p>
            </div>
          </div>

          <div class="p-4 space-y-4">
            <!-- CHAR bindings -->
            <div v-if="control.char_bindings">
              <h5 class="text-sm font-semibold mb-2 flex items-center gap-2" style="color: var(--text-primary);">
                <i class="pi pi-sitemap" style="color: var(--primary-gold);"></i>
                Implemented through
              </h5>
              <div class="flex flex-wrap gap-2 items-center text-sm">
                <span v-for="pd in control.char_bindings.process_domains || []" :key="pd"
                      class="px-2.5 py-1 rounded font-mono font-semibold"
                      style="background: var(--background-secondary); color: var(--primary-gold);">
                  {{ pd }}
                </span>
                <span v-for="bp in control.char_bindings.base_practices || []" :key="bp"
                      class="px-2.5 py-1 rounded font-mono text-xs"
                      style="background: var(--background-secondary); color: var(--text-secondary);">
                  {{ bp }}
                </span>
                <span v-if="control.char_bindings.domain_label" class="text-xs" style="color: var(--text-tertiary);">
                  {{ control.char_bindings.domain_label }}
                </span>
              </div>
            </div>

            <!-- Inputs / outputs / tooling -->
            <div class="grid gap-4 md:grid-cols-3">
              <div v-if="control.inputs?.length">
                <h5 class="text-xs font-semibold uppercase tracking-wide mb-1" style="color: var(--text-tertiary);">Inputs</h5>
                <ul class="list-disc list-inside text-sm space-y-0.5" style="color: var(--text-secondary);">
                  <li v-for="(x, i) in control.inputs" :key="i">{{ x }}</li>
                </ul>
              </div>
              <div v-if="control.outputs?.length">
                <h5 class="text-xs font-semibold uppercase tracking-wide mb-1" style="color: var(--text-tertiary);">Outputs</h5>
                <ul class="list-disc list-inside text-sm space-y-0.5" style="color: var(--text-secondary);">
                  <li v-for="(x, i) in control.outputs" :key="i">{{ x }}</li>
                </ul>
              </div>
              <div v-if="control.tooling?.length">
                <h5 class="text-xs font-semibold uppercase tracking-wide mb-1" style="color: var(--text-tertiary);">Tooling</h5>
                <div class="flex flex-wrap gap-1.5">
                  <span v-for="(t, i) in control.tooling" :key="i"
                        class="px-2 py-0.5 rounded text-xs"
                        style="background: var(--background-secondary); color: var(--text-secondary);">
                    {{ t }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Maturity ladder -->
            <div v-if="control.maturity?.levels && hasLevels(control.maturity.levels)">
              <h5 class="text-sm font-semibold mb-2 flex items-center gap-2" style="color: var(--text-primary);">
                <i class="pi pi-chart-line" style="color: var(--primary-gold);"></i>
                Maturity Levels
                <span v-if="control.maturity.minimum" class="text-xs font-normal" style="color: var(--text-tertiary);">
                  (minimum: {{ control.maturity.minimum }})
                </span>
              </h5>
              <div class="grid gap-1.5">
                <div v-for="level in MATURITY_LEVELS" :key="level"
                     v-show="control.maturity.levels?.[level]"
                     class="text-sm p-2 rounded flex items-start gap-3"
                     :style="{ background: 'var(--background-secondary)', outline: level === control.maturity.minimum ? '1px solid var(--primary-gold)' : 'none' }">
                  <span class="flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
                        style="background: var(--sage-green-light, #E8EEE8); color: var(--text-tertiary); min-width: 6.5rem; text-align: center;">
                    {{ level }}
                  </span>
                  <span style="color: var(--text-secondary);">{{ control.maturity.levels?.[level] }}</span>
                </div>
              </div>
            </div>

            <!-- Requirements addressed / evidence / assurance (collapsible) -->
            <Accordion :multiple="true">
              <AccordionTab v-if="hasRequirements(control.control_id)">
                <template #header>
                  <span class="text-sm font-semibold flex items-center gap-2">
                    <i class="pi pi-verified" style="color: var(--primary-gold);"></i>
                    External Requirements Addressed
                  </span>
                </template>
                <div class="space-y-2">
                  <div v-for="(reqs, fwId) in requirementsAddressedBy(control.control_id)" :key="fwId"
                       class="flex flex-wrap items-baseline gap-2 text-sm">
                    <span class="font-medium w-32" style="color: var(--text-primary);">{{ frameworkLabel(String(fwId)) }}</span>
                    <span v-for="r in reqs" :key="r"
                          class="px-2 py-0.5 rounded font-mono text-xs"
                          style="background: var(--background-secondary); color: var(--text-secondary);">
                      {{ r }}
                    </span>
                  </div>
                </div>
              </AccordionTab>

              <AccordionTab v-if="control.maturity?.evidence?.length">
                <template #header>
                  <span class="text-sm font-semibold flex items-center gap-2">
                    <i class="pi pi-file-check" style="color: var(--primary-gold);"></i>
                    Evidence by Level ({{ control.maturity.evidence.length }})
                  </span>
                </template>
                <div class="space-y-3">
                  <div v-for="level in MATURITY_LEVELS" :key="level"
                       v-show="evidenceAt(control, level).length">
                    <div class="text-xs font-semibold uppercase tracking-wide mb-1" style="color: var(--text-tertiary);">
                      {{ level }}
                    </div>
                    <ul class="list-disc list-inside space-y-1 text-sm" style="color: var(--text-secondary);">
                      <li v-for="(ev, i) in evidenceAt(control, level)" :key="i">
                        {{ ev.text }}
                        <span v-if="ev.mechanisms?.length" class="text-xs ml-1" style="color: var(--text-tertiary);">
                          ({{ ev.mechanisms.join(', ').replace(/_/g, ' ') }})
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionTab>

              <AccordionTab v-if="control.assurance || control.depends_on?.length || control.legacy_ids?.length || control.note">
                <template #header>
                  <span class="text-sm font-semibold flex items-center gap-2">
                    <i class="pi pi-shield" style="color: var(--primary-gold);"></i>
                    Assurance &amp; Dependencies
                  </span>
                </template>
                <dl class="text-sm grid gap-2" style="color: var(--text-secondary);">
                  <div v-if="control.assurance?.minimum" class="flex gap-2">
                    <dt class="w-40 font-medium" style="color: var(--text-primary);">Assurance minimum</dt>
                    <dd>{{ control.assurance.minimum.replace(/_/g, ' ') }}</dd>
                  </div>
                  <div v-if="control.assurance?.cadence?.default" class="flex gap-2">
                    <dt class="w-40 font-medium" style="color: var(--text-primary);">Default cadence</dt>
                    <dd>{{ control.assurance.cadence.default }}</dd>
                  </div>
                  <div v-if="control.depends_on?.length" class="flex gap-2">
                    <dt class="w-40 font-medium" style="color: var(--text-primary);">Depends on</dt>
                    <dd class="font-mono text-xs">{{ control.depends_on.join(', ') }}</dd>
                  </div>
                  <div v-if="control.legacy_ids?.length" class="flex gap-2">
                    <dt class="w-40 font-medium" style="color: var(--text-primary);">Supersedes</dt>
                    <dd class="font-mono text-xs">{{ control.legacy_ids.join(', ') }}</dd>
                  </div>
                  <div v-if="control.note" class="flex gap-2">
                    <dt class="w-40 font-medium" style="color: var(--text-primary);">Note</dt>
                    <dd class="italic">{{ control.note }}</dd>
                  </div>
                </dl>
              </AccordionTab>
            </Accordion>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="py-8" style="color: var(--text-muted);">
      <div class="flex items-start gap-3 p-4 rounded-lg" style="background: var(--background-secondary);">
        <i class="pi pi-info-circle text-xl mt-0.5" style="color: var(--primary-gold);"></i>
        <p class="text-sm" style="color: var(--text-secondary);">
          No ASHC controls are bound to this policy in the framework registry yet.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import type { Section } from '@/types/ashmatics';
import { useControls } from '@/composables/useControls';
import { MATURITY_LEVELS, type AshcControl, type MaturityLevel } from '@/composables/useCharModel';

const props = defineProps<{
  section: Section;
}>();

const { getControlsForSection, families, requirementsAddressedBy, frameworks } = useControls();

const controls = computed(() => getControlsForSection(props.section));

/** ISO 42001 Annex A clauses from navigation.json (refreshed from the registry by the backend). */
const isoClauses = computed(() => props.section?.isoControls || []);

const familyName = (family: string): string => families[family]?.name ?? family;

const frameworkLabel = (id: string): string => frameworks.find((f) => f.id === id)?.label ?? id;

const hasLevels = (levels: Partial<Record<MaturityLevel, string>>): boolean =>
  Object.values(levels).some((v) => !!v);

const hasRequirements = (controlId: string): boolean =>
  Object.keys(requirementsAddressedBy(controlId)).length > 0;

const evidenceAt = (control: AshcControl, level: MaturityLevel) =>
  (control.maturity?.evidence ?? []).filter((e) => e.level === level);
</script>

<style scoped>
:deep(.p-accordion .p-accordion-header-link) {
  padding: 0.75rem 1rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-light);
}

:deep(.p-accordion .p-accordion-content) {
  padding: 1rem;
  background: var(--background-primary);
}
</style>
